'use server'

import { apiRequest, ApiResponse } from '../request'

export type SystemAnnouncementKind = 'INFO' | 'WARNING' | 'MAINTENANCE' | 'RELEASE'

/** O que o usuário recebe: só o necessário para exibir. */
export interface PendingAnnouncement {
    id: number
    title: string
    body: string
    kind: SystemAnnouncementKind
    requiresAck: boolean
    publishedAt: string | null
}

/** O que o painel MASTER manipula. */
export interface SystemAnnouncement extends PendingAnnouncement {
    startsAt: string | null
    endsAt: string | null
    ownerOrganizationId: number | null
    organizationIds: number[]
    companyIds: number[]
    roleIds: number[]
    createdBy?: { id: number; name: string } | null
    _count?: { reads: number }
    createdAt: string
}

export interface SystemAnnouncementReader {
    readAt: string
    user: { id: number; name: string; email: string }
}

export interface SystemAnnouncementInput {
    title: string
    body: string
    kind?: SystemAnnouncementKind
    requiresAck?: boolean
    startsAt?: string | null
    endsAt?: string | null
    organizationIds?: number[]
    companyIds?: number[]
    roleIds?: number[]
    /** Publicar já na criação. Sem isto o aviso nasce rascunho. */
    publish?: boolean
}

/**
 * Avisos que este usuário ainda não viu.
 *
 * Chamado na subida do app — é o que faz quem estava offline receber ao entrar
 * depois. O websocket só antecipa esta mesma consulta.
 */
export async function getPendingAnnouncements(): Promise<ApiResponse<PendingAnnouncement[]>> {
    return apiRequest<PendingAnnouncement[]>('system-announcements/pending', {})
}

export async function acknowledgeAnnouncement(id: number): Promise<ApiResponse<unknown>> {
    return apiRequest<unknown>(`system-announcements/${id}/ack`, {}, 'post')
}

// ==================== Painel MASTER ====================

export async function getSystemAnnouncements(): Promise<ApiResponse<SystemAnnouncement[]>> {
    return apiRequest<SystemAnnouncement[]>('system-announcements', {})
}

export async function getSystemAnnouncement(id: number): Promise<ApiResponse<SystemAnnouncement>> {
    return apiRequest<SystemAnnouncement>(`system-announcements/${id}`, {})
}

export async function getAnnouncementReaders(
    id: number
): Promise<ApiResponse<SystemAnnouncementReader[]>> {
    return apiRequest<SystemAnnouncementReader[]>(`system-announcements/${id}/readers`, {})
}

export async function createSystemAnnouncement(
    data: SystemAnnouncementInput
): Promise<ApiResponse<SystemAnnouncement>> {
    return apiRequest<SystemAnnouncement>('system-announcements', { data }, 'post')
}

export async function updateSystemAnnouncement(
    id: number,
    data: Partial<SystemAnnouncementInput>
): Promise<ApiResponse<SystemAnnouncement>> {
    return apiRequest<SystemAnnouncement>(`system-announcements/${id}`, { data }, 'patch')
}

/** Publicar é separado de editar: depois dele, quem já leu não lê de novo. */
export async function publishSystemAnnouncement(
    id: number
): Promise<ApiResponse<SystemAnnouncement>> {
    return apiRequest<SystemAnnouncement>(`system-announcements/${id}/publish`, {}, 'post')
}

export async function deleteSystemAnnouncement(id: number): Promise<ApiResponse<unknown>> {
    return apiRequest<unknown>(`system-announcements/${id}`, {}, 'delete')
}
